/**
 * "I'm Safe" Check-in Component
 * One-tap crisis notification to emergency contacts
 */
import React, { useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  Heart,
  Shield,
  MapPin,
  Clock,
  Phone,
  Mail,
  Plus,
  Trash2,
  CheckCircle,
  AlertTriangle,
  Loader2,
  Settings,
  Send,
  Users
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Emergency contact management
const EmergencyContactCard = ({ contact, onRemove }) => (
  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 border border-slate-700">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-full bg-teal-500/20 flex items-center justify-center">
        <span className="text-sm font-bold text-teal-400">
          {contact.name.charAt(0).toUpperCase()}
        </span>
      </div>
      <div>
        <p className="text-sm font-medium text-white">{contact.name}</p>
        <p className="text-xs text-slate-400">{contact.phone || contact.email}</p>
      </div>
    </div>
    <Button
      variant="ghost"
      size="icon"
      onClick={() => onRemove(contact.id)}
      className="text-slate-400 hover:text-red-400"
    >
      <Trash2 className="w-4 h-4" />
    </Button>
  </div>
);

// Main I'm Safe Button Component
export const ImSafeButton = ({ variant = 'default', size = 'default' }) => {
  const { token, user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [location, setLocation] = useState(null);
  const [customMessage, setCustomMessage] = useState('');
  const [contacts, setContacts] = useState(() => {
    const saved = localStorage.getItem('veriwatch_emergency_contacts');
    return saved ? JSON.parse(saved) : [];
  });

  // Get current location
  const getCurrentLocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setLocation({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy
          });
        },
        (err) => console.error('Location error:', err),
        { enableHighAccuracy: true }
      );
    }
  }, []);

  // Send I'm Safe notification
  const sendImSafe = useCallback(async () => {
    if (contacts.length === 0) {
      alert('Please add at least one emergency contact first');
      return;
    }

    setSending(true);
    getCurrentLocation();

    try {
      // In production, this would send to the backend which would
      // then send SMS/email to emergency contacts
      const payload = {
        user_name: user?.name || 'VeriWatch User',
        message: customMessage || "I'm safe and checking in.",
        location,
        timestamp: new Date().toISOString(),
        contacts: contacts.map(c => ({ name: c.name, phone: c.phone, email: c.email }))
      };

      // Simulate API call (in production, use actual endpoint)
      if (token) {
        await fetch(`${API_URL}/api/safety/check-in`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        }).catch(() => {
          // Endpoint may not exist yet, that's okay for demo
          console.log('Check-in sent (simulated)');
        });
      }

      setSent(true);
      setTimeout(() => {
        setSent(false);
        setIsOpen(false);
        setCustomMessage('');
      }, 3000);

    } catch (error) {
      console.error('Failed to send check-in:', error);
    } finally {
      setSending(false);
    }
  }, [contacts, customMessage, location, token, user, getCurrentLocation]);

  // Quick send without opening dialog
  const quickSend = useCallback(() => {
    if (contacts.length > 0) {
      sendImSafe();
    } else {
      setIsOpen(true);
    }
  }, [contacts, sendImSafe]);

  if (variant === 'floating') {
    return (
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <Button
            className="fixed bottom-4 right-4 z-50 rounded-full w-14 h-14 bg-green-600 hover:bg-green-500 shadow-lg shadow-green-500/30"
            title="I'm Safe Check-in"
          >
            <Heart className="w-6 h-6" />
          </Button>
        </DialogTrigger>
        <DialogContent className="glass-card border-slate-800 max-w-md">
          <ImSafeDialogContent
            contacts={contacts}
            setContacts={setContacts}
            customMessage={customMessage}
            setCustomMessage={setCustomMessage}
            location={location}
            sending={sending}
            sent={sent}
            onSend={sendImSafe}
            getCurrentLocation={getCurrentLocation}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          size={size}
          className="bg-green-600 hover:bg-green-500 text-white gap-2"
        >
          <Heart className="w-4 h-4" />
          I'm Safe
        </Button>
      </DialogTrigger>
      <DialogContent className="glass-card border-slate-800 max-w-md">
        <ImSafeDialogContent
          contacts={contacts}
          setContacts={setContacts}
          customMessage={customMessage}
          setCustomMessage={setCustomMessage}
          location={location}
          sending={sending}
          sent={sent}
          onSend={sendImSafe}
          getCurrentLocation={getCurrentLocation}
        />
      </DialogContent>
    </Dialog>
  );
};

// Dialog content component
const ImSafeDialogContent = ({
  contacts,
  setContacts,
  customMessage,
  setCustomMessage,
  location,
  sending,
  sent,
  onSend,
  getCurrentLocation
}) => {
  const [showAddContact, setShowAddContact] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', phone: '', email: '' });

  const addContact = () => {
    if (!newContact.name || (!newContact.phone && !newContact.email)) return;
    
    const contact = {
      id: Date.now().toString(),
      ...newContact
    };
    
    const updatedContacts = [...contacts, contact];
    setContacts(updatedContacts);
    localStorage.setItem('veriwatch_emergency_contacts', JSON.stringify(updatedContacts));
    setNewContact({ name: '', phone: '', email: '' });
    setShowAddContact(false);
  };

  const removeContact = (id) => {
    const updatedContacts = contacts.filter(c => c.id !== id);
    setContacts(updatedContacts);
    localStorage.setItem('veriwatch_emergency_contacts', JSON.stringify(updatedContacts));
  };

  if (sent) {
    return (
      <div className="py-8 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
          <CheckCircle className="w-8 h-8 text-green-400" />
        </div>
        <h3 className="text-xl font-semibold text-white mb-2">Check-in Sent!</h3>
        <p className="text-slate-400">
          Your emergency contacts have been notified that you're safe.
        </p>
      </div>
    );
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-white">
          <Heart className="w-5 h-5 text-green-400" />
          I'm Safe Check-in
        </DialogTitle>
        <DialogDescription className="text-slate-400">
          Quickly notify your emergency contacts that you're safe
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 py-4">
        {/* Emergency Contacts */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
              <Users className="w-4 h-4 text-teal-400" />
              Emergency Contacts
            </label>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAddContact(!showAddContact)}
              className="text-teal-400 hover:text-teal-300"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </div>

          {/* Add Contact Form */}
          {showAddContact && (
            <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700 mb-3 space-y-2">
              <Input
                placeholder="Name"
                value={newContact.name}
                onChange={(e) => setNewContact(prev => ({ ...prev, name: e.target.value }))}
                className="bg-slate-900/50 border-slate-600"
              />
              <Input
                placeholder="Phone (optional)"
                value={newContact.phone}
                onChange={(e) => setNewContact(prev => ({ ...prev, phone: e.target.value }))}
                className="bg-slate-900/50 border-slate-600"
              />
              <Input
                placeholder="Email (optional)"
                value={newContact.email}
                onChange={(e) => setNewContact(prev => ({ ...prev, email: e.target.value }))}
                className="bg-slate-900/50 border-slate-600"
              />
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAddContact(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={addContact}
                  className="flex-1 bg-teal-600 hover:bg-teal-500"
                >
                  Add Contact
                </Button>
              </div>
            </div>
          )}

          {/* Contact List */}
          {contacts.length > 0 ? (
            <div className="space-y-2">
              {contacts.map(contact => (
                <EmergencyContactCard
                  key={contact.id}
                  contact={contact}
                  onRemove={removeContact}
                />
              ))}
            </div>
          ) : (
            <div className="p-4 rounded-lg bg-slate-800/30 border border-slate-700 text-center">
              <Users className="w-6 h-6 text-slate-600 mx-auto mb-2" />
              <p className="text-xs text-slate-500">No emergency contacts added</p>
            </div>
          )}
        </div>

        {/* Custom Message */}
        <div>
          <label className="text-sm font-medium text-slate-300 mb-2 block">
            Custom Message (optional)
          </label>
          <Textarea
            placeholder="I'm safe and checking in..."
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            className="bg-slate-800/50 border-slate-700 resize-none"
            rows={2}
          />
        </div>

        {/* Location */}
        <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 border border-slate-700">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-teal-400" />
            <span className="text-sm text-slate-300">
              {location ? `${location.lat.toFixed(4)}, ${location.lng.toFixed(4)}` : 'Location not available'}
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={getCurrentLocation}
            className="text-teal-400"
          >
            Update
          </Button>
        </div>

        {/* Send Button */}
        <Button
          onClick={onSend}
          disabled={sending || contacts.length === 0}
          className="w-full bg-green-600 hover:bg-green-500 h-12 text-lg"
        >
          {sending ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Send className="w-5 h-5 mr-2" />
              Send "I'm Safe" to {contacts.length} Contact{contacts.length !== 1 ? 's' : ''}
            </>
          )}
        </Button>

        {contacts.length === 0 && (
          <p className="text-xs text-amber-400 text-center">
            <AlertTriangle className="w-3 h-3 inline mr-1" />
            Add at least one emergency contact to send check-ins
          </p>
        )}
      </div>
    </>
  );
};

export default ImSafeButton;
